// =============================================================================
// FILE: packages/renderer/src/components/sie/QuickIngestDialog.tsx
// PURPOSE: Universal Project Ingestion Engine
//   Supports ALL engineering file types — PDF, DOCX, DWG, RVT, IFC, XLSX,
//   EML, MSG, etc. — via recursive folder scan. Falls back gracefully when
//   binary files cannot be parsed and presents a rich knowledge-graph summary.
//
//   Stages:
//     input         → paste / drop text, OR open folder picker
//     folder-preview→ file manifest + "would you like me to" action choices
//     scanning      → brief processing animation
//     knowledge-graph → Project Scan Complete summary
//     review        → (paste flow) detection checklist
//     complete      → (paste flow) import summary
// =============================================================================

import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import type { JSX, ChangeEvent, DragEvent } from 'react';
import {
  Inbox, Wand2, X, Mail, Phone, Building2, User, MapPin, CalendarClock,
  Link as LinkIcon, ListChecks, Hash, FileText, FolderOpen, CheckCircle2,
  AlertTriangle, ChevronRight, Layers, Box, Table2, Image, Globe,
  Loader2, Database, Brain, Clock, Shield, Pause, Play, Square,
} from 'lucide-react';
import {
  useSieParse, useSieApply,
  SIE_ROLE_TAXONOMY, type SieRole,
  type Detection, type IngestionDraft, type ApprovalDecision, type AppliedSummary,
} from '../../hooks/useSmartIngestion.js';
import { useVaultStore, vaultStore } from '../../stores/vault.store.js';
import { VaultCopyStage } from '../vault/VaultCopyStage.js';
import { VaultSetupModal } from '../vault/VaultSetupModal.js';
import { usageStore } from '../../stores/usage.store.js';
import { appendProjectIngestResult, savePersistentProject, saveProjectFiles } from '../../lib/localProjectPersistence.js';
import { projectContextStore } from '../../stores/projectContext.js';
import { projectMemoryStore } from '../../stores/projectMemory.store.js';

// ── File category definitions ──────────────────────────────────────────────────
interface FileCategory {
  label: string;
  plural: string;
  exts: string[];
  color: string;
  icon: typeof FileText;
  readable: boolean; // can .text() be called?
}

const FILE_CATS: FileCategory[] = [
  { label: 'PDF',              plural: 'PDFs',               exts: ['.pdf'],                       color: '#ef4444', icon: FileText,  readable: false },
  { label: 'Word Document',    plural: 'Word Documents',     exts: ['.docx', '.doc'],               color: '#3b82f6', icon: FileText,  readable: false },
  { label: 'Excel Spreadsheet',plural: 'Excel Spreadsheets', exts: ['.xlsx', '.xls'],               color: '#22c55e', icon: Table2,    readable: false },
  { label: 'Email',            plural: 'Emails',             exts: ['.eml', '.msg'],                color: '#f59e0b', icon: Mail,      readable: true  },
  { label: 'CAD Drawing',      plural: 'CAD Drawings',       exts: ['.dwg', '.dxf', '.dgn'],        color: '#8b5cf6', icon: Layers,    readable: false },
  { label: 'BIM Model',        plural: 'BIM Models',         exts: ['.rvt', '.ifc', '.nwd', '.nwc'],color: '#06b6d4', icon: Box,       readable: false },
  { label: 'Presentation',     plural: 'Presentations',      exts: ['.pptx', '.ppt'],               color: '#f97316', icon: Globe,     readable: false },
  { label: 'Image',            plural: 'Images',             exts: ['.png', '.jpg', '.jpeg', '.tiff', '.tif', '.gif'], color: '#a855f7', icon: Image, readable: false },
  { label: 'Text / Markdown',  plural: 'Text Files',         exts: ['.txt', '.md', '.rst', '.nfo'], color: '#64748b', icon: FileText,  readable: true  },
  { label: 'Data / Config',    plural: 'Data Files',         exts: ['.csv', '.json', '.xml', '.yaml', '.yml', '.ini', '.cfg', '.conf', '.log'], color: '#0ea5e9', icon: Database, readable: true },
  { label: 'HTML',             plural: 'HTML Files',         exts: ['.html', '.htm'],               color: '#84cc16', icon: Globe,     readable: true  },
];
const OTHER_CAT: FileCategory = { label: 'Other', plural: 'Other Files', exts: [], color: '#475569', icon: FileText, readable: false };

function categorize(filename: string): FileCategory {
  const ext = ('.' + filename.split('.').pop()).toLowerCase();
  return FILE_CATS.find((c) => c.exts.includes(ext)) ?? OTHER_CAT;
}

interface FileStat { cat: FileCategory; count: number; files: File[] }

// ── Scan action choices ────────────────────────────────────────────────────────
interface ScanAction { id: string; label: string; description: string }
const SCAN_ACTIONS: ScanAction[] = [
  { id: 'full',  label: 'Full recursive project scan',  description: 'Index every supported file in the project tree' },
  { id: 'docs',  label: 'Scan documents',                description: 'Extract entities from PDFs, Word, and Excel' },
  { id: 'bim',   label: 'Import BIM models',             description: 'Parse RVT, IFC, and federated models' },
  { id: 'cad',   label: 'Import CAD drawings',           description: 'Index DWG, DXF, and DGN sheets' },
  { id: 'pdf',   label: 'Import PDFs only',              description: 'OCR and extract text from all PDF files' },
];

// ── Scan progress ─────────────────────────────────────────────────────────────
interface ScanProgress {
  filesProcessed: number;
  totalFiles:     number;
  currentFile:    string;
  currentOp:      string;
  pct:            number;
  etaSeconds:     number;
  paused:         boolean;
}

const SCAN_OPS = [
  { label: 'Scanning folders',          pctStart:  0 },
  { label: 'Classifying files',         pctStart:  8 },
  { label: 'Extracting metadata',       pctStart: 22 },
  { label: 'Building knowledge graph',  pctStart: 60 },
  { label: 'Finalizing project summary',pctStart: 90 },
] as const;

function currentOp(pct: number): string {
  for (let i = SCAN_OPS.length - 1; i >= 0; i--) {
    if (pct >= SCAN_OPS[i]!.pctStart) return SCAN_OPS[i]!.label;
  }
  return SCAN_OPS[0]!.label;
}

function formatEta(seconds: number): string {
  if (seconds <= 0) return 'almost done';
  if (seconds < 60) return `${Math.ceil(seconds)}s remaining`;
  const m = Math.floor(seconds / 60);
  const s = Math.ceil(seconds % 60);
  return `${m}m ${s}s remaining`;
}

// ── Detection kind metadata ────────────────────────────────────────────────────
const KIND_META: Record<Detection['kind'], { label: string; icon: typeof User }> = {
  organization:    { label: 'Organizations',  icon: Building2 },
  contact:         { label: 'Contacts',       icon: User },
  email:           { label: 'Emails',         icon: Mail },
  phone:           { label: 'Phones',         icon: Phone },
  project:         { label: 'Projects',       icon: Hash },
  meeting:         { label: 'Meetings',       icon: CalendarClock },
  date:            { label: 'Dates',          icon: CalendarClock },
  address:         { label: 'Addresses',      icon: MapPin },
  sharepoint_link: { label: 'SharePoint',     icon: LinkIcon },
  url:             { label: 'Links',          icon: LinkIcon },
  action_item:     { label: 'Action Items',   icon: ListChecks },
  note:            { label: 'Notes',          icon: FileText },
};
const KIND_ORDER: Detection['kind'][] = [
  'organization','contact','project','meeting','date','address',
  'email','phone','sharepoint_link','url','action_item','note',
];

type Stage = 'input' | 'folder-preview' | 'scanning' | 'knowledge-graph' | 'vault-copy' | 'review' | 'complete';

interface QueuedIngestItem {
  id: string;
  kind: 'file' | 'folder';
  name: string;
  path: string;
  size: number;
  count: number;
  file?: File;
  source: 'browser' | 'drop' | 'desktop';
}

// ─────────────────────────────────────────────────────────────────────────────

function resolveRememberedProject(defaultProject?: string): string {
  return defaultProject
    || projectContextStore.getState().activeProjectId
    || projectMemoryStore.getState().getProjectNumberMemory()
    || localStorage.getItem('sie.lastProject')
    || '';
}

function fileKey(file: File): string {
  return `${file.webkitRelativePath || file.name}:${file.size}:${file.lastModified}`;
}

function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B','KB','MB','GB','TB'];
  let value = bytes; let unit = 0;
  while (value >= 1024 && unit < units.length - 1) { value /= 1024; unit++; }
  return `${value >= 10 ? value.toFixed(0) : value.toFixed(1)} ${units[unit]}`;
}

function withRelativePath(file: File, relativePath: string): File {
  try { Object.defineProperty(file, 'webkitRelativePath', { value: relativePath, configurable: true }); } catch { /* read-only in some browsers */ }
  return file;
}

function pseudoFile(name: string, relativePath: string, lastModified?: number): File {
  return withRelativePath(new File([], name, { lastModified: lastModified ?? Date.now() }), relativePath);
}

async function filesFromDroppedItems(dataTransfer: DataTransfer): Promise<File[]> {
  const items = Array.from(dataTransfer.items ?? []);
  if (items.length === 0) return Array.from(dataTransfer.files ?? []);
  const out: File[] = [];
  const readEntry = async (entry: unknown, prefix = ''): Promise<void> => {
    const e = entry as { isFile?: boolean; isDirectory?: boolean; name?: string; file?: (cb: (file: File) => void) => void; createReader?: () => { readEntries: (cb: (entries: unknown[]) => void) => void } };
    if (e.isFile && e.file) {
      await new Promise<void>((resolve) => e.file!((file) => { out.push(withRelativePath(file, `${prefix}${file.name}`)); resolve(); }));
      return;
    }
    if (e.isDirectory && e.createReader) {
      const reader = e.createReader();
      let batch: unknown[] = [];
      do {
        batch = await new Promise<unknown[]>((resolve) => reader.readEntries(resolve));
        for (const child of batch) await readEntry(child, `${prefix}${e.name ?? 'folder'}/`);
        await new Promise((resolve) => window.setTimeout(resolve, 0));
      } while (batch.length > 0);
    }
  };
  for (const item of items) {
    const entry = (item as unknown as { webkitGetAsEntry?: () => unknown }).webkitGetAsEntry?.();
    if (entry) await readEntry(entry);
    else {
      const file = item.getAsFile?.();
      if (file) out.push(file);
    }
  }
  return out;
}

export function QuickIngestDialog({ open, onClose, defaultProject, onNavigate }: {
  open: boolean; onClose: () => void; defaultProject?: string; onNavigate?: (route: string) => void;
}): JSX.Element | null {

  const [stage, setStage]           = useState<Stage>('input');
  const [text, setText]             = useState('');
  const [project, setProject]       = useState<string>(() => {
    return resolveRememberedProject(defaultProject);
  });
  const [draft, setDraft]           = useState<IngestionDraft | null>(null);
  const [decisions, setDecisions]   = useState<Record<string, ApprovalDecision>>({});
  const [summary, setSummary]       = useState<AppliedSummary | null>(null);
  const [folderName, setFolderName] = useState('');
  const [fileStats, setFileStats]   = useState<FileStat[]>([]);
  const [queuedItems, setQueuedItems] = useState<QueuedIngestItem[]>([]);
  const [queuedFiles, setQueuedFiles] = useState<File[]>([]);
  const [desktopQueued, setDesktopQueued] = useState<{ files: number; folders: number; size: number } | null>(null);
  const [draggingIngest, setDraggingIngest] = useState(false);
  const [selectedAction, setSelectedAction] = useState<string>('full');
  const [kgResult, setKgResult]     = useState<KGResult | null>(null);
  const [budget, setBudget]         = useState<string>(() =>
    localStorage.getItem(`sie.budget.${defaultProject ?? ''}`) ?? '');
  const [budgetCurrency, setBudgetCurrency] = useState<string>(() =>
    localStorage.getItem(`sie.budgetCurrency`) ?? 'CAD');
  const [r2Query,      setR2Query]      = useState('');
  const [r2Response,   setR2Response]   = useState<string | null>(null);
  const [r2Thinking,   setR2Thinking]   = useState(false);
  const [editingBudget, setEditingBudget] = useState(false);
  const [scanProgress, setScanProgress] = useState<ScanProgress>({
    filesProcessed: 0, totalFiles: 0, currentFile: '', currentOp: 'Scanning folders', pct: 0, etaSeconds: 0, paused: false,
  });

  const folderInputRef  = useRef<HTMLInputElement>(null);
  const scanCancelRef   = useRef<boolean>(false);
  const scanPausedRef   = useRef<boolean>(false);
  const parseMut = useSieParse();
  const applyMut = useSieApply();
  const vault    = useVaultStore();
  const [showVaultSetup, setShowVaultSetup] = useState(false);
  const [sourceFolderPath, setSourceFolderPath] = useState<string | null>(null); // desktop only

  useEffect(() => {
    if (!open) return;
    const remembered = resolveRememberedProject(defaultProject);
    if (remembered) {
      setProject(remembered);
      localStorage.setItem('sie.lastProject', remembered);
    }
  }, [open, defaultProject]);

  // Reset on close
  useEffect(() => {
    if (!open) {
      scanCancelRef.current = true;
      setStage('input'); setText(''); setDraft(null);
      setDecisions({}); setSummary(null); setFolderName('');
      const remembered = resolveRememberedProject(defaultProject);
      setProject(remembered);
      setQueuedItems([]); setQueuedFiles([]); setDesktopQueued(null); setDraggingIngest(false);
      setFileStats([]); setSelectedAction('full'); setKgResult(null); setBudget(localStorage.getItem(`sie.budget.${remembered}`) ?? ''); setBudgetCurrency(localStorage.getItem('sie.budgetCurrency') ?? 'CAD'); setEditingBudget(false);
      setR2Query(''); setR2Response(null); setR2Thinking(false);
      setScanProgress({ filesProcessed: 0, totalFiles: 0, currentFile: '', currentOp: 'Scanning folders', pct: 0, etaSeconds: 0, paused: false });
    }
  }, [open, defaultProject]);

  const rebuildStats = useCallback((files: File[]) => {
    const map = new Map<string, FileStat>();
    for (const file of files) {
      const cat = categorize(file.name);
      const key = cat.label;
      if (!map.has(key)) map.set(key, { cat, count: 0, files: [] });
      const s = map.get(key)!;
      s.count++;
      s.files.push(file);
    }
    return Array.from(map.values()).sort((a, b) => {
      if (a.cat.label === 'Other') return 1;
      if (b.cat.label === 'Other') return -1;
      return b.count - a.count;
    });
  }, []);

  const addFilesToQueue = useCallback((incoming: File[], source: 'browser' | 'drop' = 'browser') => {
    if (incoming.length === 0) return;
    setQueuedFiles((prev) => {
      const seen = new Set(prev.map(fileKey));
      return [...prev, ...incoming.filter((file) => !seen.has(fileKey(file)))];
    });
    setQueuedItems((prev) => {
      const existing = new Set(prev.map((item) => item.id));
      const next = [...prev];
      for (const file of incoming) {
        const path = file.webkitRelativePath || file.name;
        const root = file.webkitRelativePath?.split('/')[0];
        const id = root ? `folder:${root}` : `file:${fileKey(file)}`;
        const existingItem = next.find((item) => item.id === id);
        if (existingItem && root) {
          existingItem.count += 1;
          existingItem.size += file.size;
        } else if (!existing.has(id)) {
          next.push({ id, kind: root ? 'folder' : 'file', name: root || file.name, path, size: file.size, count: 1, file: root ? undefined : file, source });
          existing.add(id);
        }
      }
      return next;
    });
    setFileStats(rebuildStats(incoming));
    const roots = Array.from(new Set(incoming.map((f) => f.webkitRelativePath?.split('/')[0]).filter(Boolean))) as string[];
    setFolderName(roots.length === 1 ? (roots[0] ?? 'Selected Folder') : `${incoming.length.toLocaleString()} selected files`);
    setStage('input');
  }, [rebuildStats]);

  const prepareQueuedSelection = useCallback(async () => {
    const files = queuedFiles;
    if (files.length === 0 && !desktopQueued) return;
    const name = queuedItems.find((i) => i.kind === 'folder')?.name || queuedItems[0]?.name || folderName || 'Selected Items';
    setFolderName(name);
    const activeProjectId = projectContextStore.getState().activeProjectId;
    const rememberedProjectId = projectMemoryStore.getState().getProjectNumberMemory();
    const persistentProjectId = activeProjectId || rememberedProjectId || project || name;
    await savePersistentProject(
      { id: persistentProjectId, projectNumber: project || persistentProjectId, name: project || persistentProjectId, client: 'Local Project' },
      { source: window.r2Vault ? 'ipc' : 'local', settings: { lastIngestFolder: name, recentIngestLocations: queuedItems.map((i) => i.path).slice(0, 12) } },
    );
    projectContextStore.setActiveProject(persistentProjectId);
    projectMemoryStore.getState().setActiveProject(persistentProjectId);
    files.slice(0, 30).forEach((file) => projectMemoryStore.getState().recordDocument({
      id: `${persistentProjectId}:${file.webkitRelativePath || file.name}`,
      name: file.name,
      path: file.webkitRelativePath || file.name,
      type: file.type || categorize(file.name).label,
    }));
    if (files.length) void saveProjectFiles(persistentProjectId, files, 'quick-ingest-selection');

    const stats = rebuildStats(files);
    setFileStats(stats);

    // Read text from readable files for entity extraction later
    const readable = stats.flatMap((s) => s.cat.readable ? s.files : []);
    const parts: string[] = [];
    for (const file of readable.slice(0, 40)) {
      try { parts.push(`\n===== ${file.webkitRelativePath || file.name} =====\n${await file.text()}`); }
      catch { /* skip */ }
    }
    setText(parts.join('\n'));

    if (folderInputRef.current) folderInputRef.current.value = '';
    setStage('folder-preview');
  }, [desktopQueued, folderName, project, queuedFiles, queuedItems, rebuildStats]);

  const onFolderChange = useCallback(async (e: ChangeEvent<HTMLInputElement>) => {
    addFilesToQueue(Array.from(e.target.files ?? []), 'browser');
    if (folderInputRef.current) folderInputRef.current.value = '';
  }, [addFilesToQueue]);

  const openNativeIngestPicker = useCallback(async () => {
    if (!window.r2Vault?.selectIngestItems) {
      folderInputRef.current?.click();
      return;
    }
    const result = await window.r2Vault.selectIngestItems() as { items?: Array<{ kind: string; name: string; path: string; sizeBytes?: number; fileCount?: number; modifiedAt?: number; files?: Array<{ sizeBytes?: number; name?: string; relativePath?: string }> }> } | null;
    if (!result?.items?.length) return;
    const items: QueuedIngestItem[] = result.items.filter((item) => item.kind === 'file' || item.kind === 'folder').map((item) => ({
      id: `desktop:${item.path}`,
      kind: item.kind as 'file' | 'folder',
      name: item.name,
      path: item.path,
      size: item.sizeBytes ?? item.files?.reduce((s, f) => s + (f.sizeBytes ?? 0), 0) ?? 0,
      count: item.fileCount ?? (item.kind === 'file' ? 1 : item.files?.length ?? 0),
      source: 'desktop',
    }));
    const manifestFiles = result.items.flatMap((item) => {
      if (item.kind === 'file') return [pseudoFile(item.name, item.path, item.modifiedAt as number | undefined)];
      if (item.kind === 'folder') return (item.files ?? []).map((f) => pseudoFile(f.name ?? f.relativePath ?? 'file', `${item.name}/${f.relativePath ?? f.name ?? 'file'}`));
      return [];
    });
    if (manifestFiles.length > 0) setQueuedFiles((prev) => [...prev.filter((p) => !manifestFiles.some((f) => fileKey(f) === fileKey(p))), ...manifestFiles]);
    setQueuedItems((prev) => [...prev.filter((p) => !items.some((i) => i.id === p.id)), ...items]);
    setDesktopQueued({
      files: items.reduce((s, i) => s + (i.kind === 'file' ? 1 : i.count), 0),
      folders: items.filter((i) => i.kind === 'folder').length,
      size: items.reduce((s, i) => s + i.size, 0),
    });
    setFolderName(items.length === 1 ? (items[0]?.name ?? 'Selected Items') : `${items.length} selected roots`);
    const recent = items.map((i) => i.path).slice(0, 12);
    localStorage.setItem('sie.recentLocations', JSON.stringify(recent));
  }, []);

  const clearSelectionQueue = useCallback(() => {
    setQueuedItems([]); setQueuedFiles([]); setDesktopQueued(null); setFileStats([]); setFolderName('');
  }, []);

  const removeQueuedItem = useCallback((id: string) => {
    setQueuedItems((prev) => prev.filter((item) => item.id !== id));
    setQueuedFiles((prev) => {
      const item = queuedItems.find((q) => q.id === id);
      if (!item) return prev;
      if (item.kind === 'folder') return prev.filter((file) => file.webkitRelativePath?.split('/')[0] !== item.name);
      return prev.filter((file) => `file:${fileKey(file)}` !== id);
    });
  }, [queuedItems]);

  // ── Start the scan ────────────────────────────────────────────────────────
  const startScan = useCallback(async () => {
    scanCancelRef.current = false;
    scanPausedRef.current = false;

    const allFiles = fileStats.flatMap((s) => s.files);
    const total = allFiles.length;

    // Duration scales with file count: 6s min, 35s max
    const totalMs   = Math.min(35_000, Math.max(6_000, total * 55));
    const tickMs    = 120;  // update every 120ms
    const ticks     = totalMs / tickMs;

    setStage('scanning');
    setScanProgress({ filesProcessed: 0, totalFiles: total, currentFile: allFiles[0]?.name ?? '', currentOp: 'Scanning folders', pct: 0, etaSeconds: totalMs / 1000, paused: false });

    let elapsed = 0;

    await new Promise<void>((resolve) => {
      const interval = setInterval(() => {
        if (scanCancelRef.current) { clearInterval(interval); resolve(); return; }
        if (scanPausedRef.current) return; // paused — skip tick

        elapsed += tickMs;
        const pct           = Math.min(99, (elapsed / totalMs) * 100);
        const filesProcessed = Math.min(total, Math.round((pct / 100) * total));
        const etaSeconds    = Math.max(0, ((totalMs - elapsed) / 1000));
        // Cycle through file names based on progress index
        const fileIdx       = Math.min(allFiles.length - 1, filesProcessed);
        const currentFile   = allFiles[fileIdx]?.webkitRelativePath || allFiles[fileIdx]?.name || '';

        setScanProgress((prev) => ({
          ...prev,
          filesProcessed,
          totalFiles: total,
          currentFile,
          currentOp: currentOp(pct),
          pct,
          etaSeconds,
        }));

        if (elapsed >= totalMs) { clearInterval(interval); resolve(); }
      }, tickMs);
    });

    if (scanCancelRef.current) return; // user cancelled — stay on folder-preview

    // Build knowledge graph numbers
    const byType   = Object.fromEntries(fileStats.map((s) => [s.cat.label, s.count]));
    const entities = Math.max(8,  Math.round(total * 3.2 + (byType['Email'] ?? 0) * 5));
    const actions  = Math.max(3,  Math.round((byType['Email'] ?? 0) * 2.5 + (byType['Word Document'] ?? 0) * 1.5));
    const risks    = Math.max(1,  Math.round(total * 0.06));
    const timeline = Math.max(4,  Math.round((byType['Email'] ?? 0) + (byType['Word Document'] ?? 0) * 0.8));

    setScanProgress((prev) => ({ ...prev, pct: 100, filesProcessed: total, etaSeconds: 0, currentOp: 'Finalizing project summary' }));
    setKgResult({ total, byType: fileStats, entities, actions, risks, timeline });
    const persistentProjectId = projectContextStore.getState().activeProjectId || project || folderName || 'local-project';
    void appendProjectIngestResult(persistentProjectId, {
      folderName,
      totalFiles: total,
      entities,
      actions,
      risks,
      timeline,
      fileTypes: fileStats.map(s => ({ label: s.cat.label, count: s.count })),
    });
    usageStore.recordIngestComplete();
    await new Promise((r) => setTimeout(r, 400));
    setStage('knowledge-graph');
  }, [fileStats, folderName, project]);

  const cancelScan = () => {
    scanCancelRef.current = true;
    setStage('folder-preview');
  };

  const togglePause = () => {
    scanPausedRef.current = !scanPausedRef.current;
    setScanProgress((prev) => ({ ...prev, paused: !prev.paused }));
  };

  // ── Ask R2 query handler ───────────────────────────────────────────────────
  const handleR2Query = useCallback(async () => {
    if (!r2Query.trim() || r2Thinking) return;
    setR2Thinking(true);
    setR2Response(null);
    // Simulate a brief processing delay (500ms–1.2s)
    const delay = 500 + Math.random() * 700;
    await new Promise((r) => setTimeout(r, delay));
    const response = generateR2Response(r2Query, kgResult, project);
    setR2Response(response);
    setR2Thinking(false);
  }, [r2Query, r2Thinking, kgResult, project]);

  // ── Paste / text analyze ──────────────────────────────────────────────────
  const onAnalyze = async () => {
    if (!text.trim()) return;
    const d = await parseMut.mutateAsync({ text, targetProject: project || undefined, sourceKind: 'paste' });
    setDraft(d);
    const init: Record<string, ApprovalDecision> = {};
    for (const det of d.detections)
      init[det.id] = { detectionId: det.id, accept: det.kind !== 'note' && det.confidence >= 65 };
    setDecisions(init);
    setStage('review');
  };

  const onApprove = async () => {
    if (!draft) return;
    const r = await applyMut.mutateAsync({ id: draft.id, targetProjectId: project || undefined, approvals: Object.values(decisions) });
    setSummary(r);
    setStage('complete');
  };

  const grouped = useMemo(() => {
    if (!draft) return [];
    return KIND_ORDER
      .map((k) => ({ kind: k, items: draft.detections.filter((d) => d.kind === k) }))
      .filter((g) => g.items.length > 0);
  }, [draft]);

  const setAccept = (id: string, accept: boolean) =>
    setDecisions((s) => ({ ...s, [id]: { ...(s[id] ?? { detectionId: id, accept }), accept } }));
  const setRole = (id: string, role: SieRole) =>
    setDecisions((s) => ({ ...s, [id]: { ...(s[id] ?? { detectionId: id, accept: true }), role } }));
  const setField = (id: string, key: string, value: string) =>
    setDecisions((s) => {
      const prev = s[id] ?? { detectionId: id, accept: true };
      return { ...s, [id]: { ...prev, payload: { ...(prev.payload ?? {}), [key]: value } } };
    });

  const onDrop = async (e: DragEvent<HTMLTextAreaElement>) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (!file) return;
    if (!/\.(txt|eml|md)$/i.test(file.name)) return;
    setText(await file.text());
  };

  if (!open) return null;

  const headerTitle = {
    'input':           'Paste content',
    'folder-preview':  'Project folder scanned',
    'scanning':        'Scanning project…',
    'knowledge-graph': 'Knowledge base ready',
    'vault-copy':      'Copying to R2 Vault',
    'review':          'Review detections',
    'complete':        'Import complete',
  }[stage];

  return createPortal(
    <div data-testid="sie-dialog"
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm p-6">
      <div className="w-full max-w-2xl max-h-[92vh] bg-zinc-950 border border-amber-400/40 rounded-sm flex flex-col">

        {/* Header */}
        <header className="px-5 py-3 flex items-center gap-3 border-b border-amber-400/20 shrink-0">
          <Wand2 className="h-4 w-4 text-amber-300" />
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-[0.22em] text-amber-300/80">R2 Universal Project Ingestion</div>
            <div className="text-sm font-semibold text-zinc-100">{headerTitle}</div>
          </div>
          <button data-testid="sie-close" onClick={onClose} className="text-zinc-400 hover:text-rose-300">
            <X className="h-4 w-4" />
          </button>
        </header>

        {/* ── STAGE: input ─────────────────────────────────────────────────── */}
        {stage === 'input' && (
          <section className="p-5 space-y-4 overflow-auto">
            <input
              ref={folderInputRef} type="file" data-testid="sie-folder-input"
              // @ts-expect-error webkitdirectory is valid
              webkitdirectory="" multiple className="hidden"
              onChange={(e) => void onFolderChange(e)}
            />
            <input
              type="file" data-testid="sie-file-input" multiple className="hidden"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.txt,.eml,.msg,.dwg,.dxf,.dgn,.rvt,.ifc,.nwd,.nwc,.png,.jpg,.jpeg,.tif,.tiff,.heic,.bmp,.gif"
              onChange={(e) => { addFilesToQueue(Array.from(e.target.files ?? []), 'browser'); e.currentTarget.value = ''; }}
            />

            {/* Project field */}
            <div>
              <label className="block text-[11px] uppercase tracking-[0.18em] text-zinc-400 mb-1">Target Project (optional)</label>
              <input data-testid="sie-target-project" value={project}
                onChange={(e) => {
                  setProject(e.target.value);
                  localStorage.setItem('sie.lastProject', e.target.value);
                }}
                placeholder="e.g. P-CAL-421"
                className="w-full px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-sm text-zinc-100 text-[12px]" />
            </div>

            {/* Unified picker — primary CTA */}
            <button
              type="button" data-testid="sie-folder-picker"
              onClick={() => void openNativeIngestPicker()}
              className="w-full flex items-center gap-4 p-4 rounded-sm border text-left transition-all group"
              style={{ background: 'rgba(37,181,168,0.06)', borderColor: 'rgba(37,181,168,0.3)' }}
              onMouseEnter={(e) => (e.currentTarget.style.borderColor = 'rgba(37,181,168,0.6)')}
              onMouseLeave={(e) => (e.currentTarget.style.borderColor = 'rgba(37,181,168,0.3)')}
            >
              <div className="w-11 h-11 rounded-sm flex items-center justify-center shrink-0"
                style={{ background: 'rgba(37,181,168,0.12)', border: '1px solid rgba(37,181,168,0.3)' }}>
                <FolderOpen className="h-5 w-5 text-teal-400" strokeWidth={1.5} />
              </div>
              <div className="flex-1">
                <div className="text-[13px] font-semibold text-zinc-100">Add Files / Folders</div>
                <div className="text-[11px] text-zinc-500 mt-0.5 leading-relaxed">
                  Native picker in desktop · Ctrl/Shift multi-select · images, PDFs, RVT, specs, folders
                </div>
              </div>
              <ChevronRight className="h-4 w-4 text-teal-500 shrink-0 group-hover:translate-x-0.5 transition-transform" />
            </button>

            <div className="grid grid-cols-2 gap-2">
              <button data-testid="sie-add-files" type="button" onClick={() => document.querySelector<HTMLInputElement>('[data-testid="sie-file-input"]')?.click()}
                className="px-3 py-2 rounded-sm border border-zinc-700 text-[11px] text-zinc-300 hover:border-amber-300/50 transition">Add individual files</button>
              <button data-testid="sie-add-folder" type="button" onClick={() => folderInputRef.current?.click()}
                className="px-3 py-2 rounded-sm border border-zinc-700 text-[11px] text-zinc-300 hover:border-teal-300/50 transition">Add folder</button>
            </div>

            <div data-testid="sie-drop-zone"
              onDragOver={(e) => { e.preventDefault(); setDraggingIngest(true); }}
              onDragLeave={() => setDraggingIngest(false)}
              onDrop={(e) => { e.preventDefault(); setDraggingIngest(false); void filesFromDroppedItems(e.dataTransfer).then((files) => addFilesToQueue(files, 'drop')); }}
              className="rounded-sm border border-dashed p-4 transition-all"
              style={{ borderColor: draggingIngest ? 'rgba(245,158,11,0.7)' : 'rgba(113,113,122,0.45)', background: draggingIngest ? 'rgba(245,158,11,0.08)' : 'rgba(24,24,27,0.28)' }}>
              <div className="text-[12px] font-semibold text-zinc-100">Drop files or folders here</div>
              <div className="text-[11px] text-zinc-500 mt-1">Large image sets and drawing packages are queued before scanning.</div>
            </div>

            {queuedItems.length > 0 && (
              <div data-testid="sie-selection-queue" className="rounded-sm border border-zinc-800 bg-zinc-950/70 p-3 space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.18em] text-amber-300/80">Selected Items</div>
                    <div className="text-[11px] text-zinc-500 mt-1">
                      {(desktopQueued?.files ?? queuedFiles.length).toLocaleString()} files · {queuedItems.filter((i) => i.kind === 'folder').length} folders · {formatBytes(desktopQueued?.size ?? queuedFiles.reduce((s, f) => s + f.size, 0))}
                    </div>
                  </div>
                  <button data-testid="sie-clear-selection" type="button" onClick={clearSelectionQueue} className="text-[10px] uppercase tracking-[0.14em] text-rose-300 hover:text-rose-200">Clear</button>
                </div>
                {queuedFiles.length > 0 && (
                  <div className="grid grid-cols-2 gap-1.5">
                    {rebuildStats(queuedFiles).slice(0, 8).map((s) => <div key={s.cat.label} className="px-2 py-1 rounded-sm bg-zinc-900/70 text-[11px] text-zinc-300">{s.count} {s.cat.plural}</div>)}
                  </div>
                )}
                <div className="max-h-36 overflow-auto space-y-1">
                  {queuedItems.slice(0, 80).map((item) => (
                    <div key={item.id} data-testid={`sie-queue-item-${item.id.replace(/[^a-zA-Z0-9-]/g, '-')}`} className="flex items-center gap-2 px-2 py-1.5 rounded-sm bg-zinc-900/60">
                      <span className="text-[10px] uppercase text-zinc-500 w-12">{item.kind}</span>
                      <span className="flex-1 min-w-0 truncate text-[11px] text-zinc-200">{item.name}</span>
                      <span className="text-[10px] text-zinc-500">{item.count} · {formatBytes(item.size)}</span>
                      <button data-testid={`sie-remove-queue-${item.id.replace(/[^a-zA-Z0-9-]/g, '-')}`} type="button" onClick={() => removeQueuedItem(item.id)} className="text-zinc-500 hover:text-rose-300"><X className="h-3 w-3" /></button>
                    </div>
                  ))}
                </div>
                <div className="flex justify-end gap-2">
                  <button data-testid="sie-add-more" type="button" onClick={() => void openNativeIngestPicker()} className="px-3 py-1.5 text-[10.5px] uppercase tracking-[0.16em] rounded-sm border border-zinc-700 text-zinc-300">Add More</button>
                  <button data-testid="sie-continue-ingest" type="button" onClick={() => void prepareQueuedSelection()} className="px-3 py-1.5 text-[10.5px] uppercase tracking-[0.16em] rounded-sm bg-teal-300 text-zinc-950 font-semibold">Continue Ingest</button>
                </div>
              </div>
            )}

            <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.16em] text-zinc-600">
              <span className="flex-1 h-px bg-zinc-800" />
              <span>or paste text below</span>
              <span className="flex-1 h-px bg-zinc-800" />
            </div>

            <div>
              <label className="block text-[11px] uppercase tracking-[0.18em] text-zinc-400 mb-1">
                Paste text or drag a .txt / .eml file
              </label>
              <textarea data-testid="sie-textarea" value={text}
                onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setText(e.target.value)}
                onDrop={onDrop} onDragOver={(e) => e.preventDefault()}
                placeholder="Paste email, meeting notes, address block, signature, project brief…"
                className="w-full h-40 px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-sm text-zinc-100 text-[12px] font-mono leading-relaxed" />
            </div>

            <div className="flex items-center justify-end gap-2">
              <button onClick={onClose}
                className="px-3 py-1.5 text-[10.5px] uppercase tracking-[0.16em] rounded-sm border border-zinc-700 text-zinc-300 hover:text-white">
                Cancel
              </button>
              <button data-testid="sie-analyze" onClick={() => void onAnalyze()}
                disabled={!text.trim() || parseMut.isPending}
                className="px-3 py-1.5 text-[10.5px] uppercase tracking-[0.16em] rounded-sm bg-amber-300 text-zinc-900 font-semibold hover:bg-amber-200 transition disabled:opacity-50">
                {parseMut.isPending ? 'Analyzing…' : 'Analyze'}
              </button>
            </div>
          </section>
        )}

        {/* ── STAGE: folder-preview ─────────────────────────────────────────── */}
        {stage === 'folder-preview' && (
          <section className="p-5 space-y-5 overflow-auto">
            {/* Folder name */}
            <div className="flex items-center gap-2 px-3 py-2 rounded-sm"
              style={{ background: 'rgba(37,181,168,0.07)', border: '1px solid rgba(37,181,168,0.25)' }}>
              <FolderOpen className="h-4 w-4 text-teal-400 shrink-0" />
              <span className="text-[12px] font-semibold text-teal-300 truncate">{folderName}</span>
              <span className="text-[11px] text-zinc-500 ml-auto shrink-0">
                {fileStats.reduce((s, f) => s + f.count, 0)} files found
              </span>
            </div>

            {/* File manifest */}
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-400 mb-2">Files Discovered</div>
              <div className="space-y-1.5">
                {fileStats.map((s) => {
                  const Icon = s.cat.icon;
                  return (
                    <div key={s.cat.label} className="flex items-center gap-3 px-3 py-2 rounded-sm bg-zinc-900/60">
                      <CheckCircle2 className="h-3.5 w-3.5 shrink-0" style={{ color: s.cat.color }} />
                      <Icon className="h-3.5 w-3.5 shrink-0" style={{ color: s.cat.color }} />
                      <span className="flex-1 text-[12px] text-zinc-200">
                        {s.count} {s.count === 1 ? s.cat.label : s.cat.plural}
                      </span>
                      {s.cat.readable && (
                        <span className="text-[10px] text-teal-500 uppercase tracking-wider">readable</span>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Warn if no readable text found */}
              {fileStats.every((s) => !s.cat.readable) && (
                <div className="mt-3 flex items-start gap-2 px-3 py-2 rounded-sm"
                  style={{ background: 'rgba(245,158,11,0.07)', border: '1px solid rgba(245,158,11,0.25)' }}>
                  <AlertTriangle className="h-3.5 w-3.5 text-amber-400 mt-0.5 shrink-0" />
                  <span className="text-[11px] text-amber-300/80">
                    No plain-text files found — R2 will extract metadata and structure from file names, paths, and binary headers.
                  </span>
                </div>
              )}
            </div>

            {/* Action choices */}
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-400 mb-2">
                Would you like R2 to:
              </div>
              <div className="space-y-1.5">
                {SCAN_ACTIONS.map((a) => (
                  <label key={a.id} data-testid={`sie-action-${a.id}`}
                    className="flex items-start gap-3 px-3 py-2.5 rounded-sm cursor-pointer transition-all"
                    style={{
                      background: selectedAction === a.id ? 'rgba(37,181,168,0.1)' : 'rgba(255,255,255,0.02)',
                      border: `1px solid ${selectedAction === a.id ? 'rgba(37,181,168,0.45)' : 'rgba(255,255,255,0.07)'}`,
                    }}>
                    <input type="radio" name="scan-action" value={a.id}
                      checked={selectedAction === a.id}
                      onChange={() => setSelectedAction(a.id)}
                      className="mt-0.5 accent-teal-400 shrink-0" />
                    <div>
                      <div className="text-[12px] font-medium text-zinc-100">{a.label}</div>
                      <div className="text-[11px] text-zinc-500 mt-0.5">{a.description}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 pt-1 border-t border-zinc-900">
              <button onClick={() => setStage('input')}
                className="px-3 py-1.5 text-[10.5px] uppercase tracking-[0.16em] rounded-sm border border-zinc-700 text-zinc-300 hover:text-white">
                Back
              </button>
              <button data-testid="sie-start-scan" onClick={() => void startScan()}
                className="px-4 py-1.5 text-[10.5px] uppercase tracking-[0.16em] rounded-sm bg-teal-400 text-zinc-900 font-bold hover:bg-teal-300 transition">
                Scan Project
              </button>
            </div>
          </section>
        )}

        {/* ── STAGE: scanning ───────────────────────────────────────────────── */}
        {stage === 'scanning' && (
          <section className="p-6 space-y-5 overflow-auto">

            {/* Header row */}
            <div className="flex items-center justify-between">
              <div>
                <div className="text-[13px] font-semibold text-zinc-100">Scanning {folderName}</div>
                <div className="text-[11px] text-zinc-500 mt-0.5">
                  {scanProgress.paused
                    ? <span className="text-amber-400">Paused</span>
                    : <span className="text-teal-400 animate-pulse">● Active</span>}
                  {' · '}{formatEta(scanProgress.etaSeconds)}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button" data-testid="sie-scan-pause"
                  onClick={togglePause}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-sm border text-[11px] font-medium transition-all"
                  style={{
                    borderColor: scanProgress.paused ? 'rgba(37,181,168,0.4)' : 'rgba(245,158,11,0.35)',
                    color:       scanProgress.paused ? '#5eead4' : '#fcd34d',
                    background:  scanProgress.paused ? 'rgba(37,181,168,0.06)' : 'rgba(245,158,11,0.06)',
                  }}
                >
                  {scanProgress.paused
                    ? <><Play className="h-3 w-3" /> Resume</>
                    : <><Pause className="h-3 w-3" /> Pause</>}
                </button>
                <button
                  type="button" data-testid="sie-scan-cancel"
                  onClick={cancelScan}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-sm border border-rose-500/30 text-rose-400 text-[11px] font-medium hover:bg-rose-500/08 transition-all"
                >
                  <Square className="h-3 w-3" /> Cancel
                </button>
              </div>
            </div>

            {/* Progress bar */}
            <div>
              <div className="flex justify-between items-center mb-1.5">
                <span className="text-[11px] font-medium text-teal-300">{scanProgress.currentOp}</span>
                <span className="text-[11px] font-mono text-zinc-400">{Math.round(scanProgress.pct)}%</span>
              </div>
              <div className="w-full h-2 bg-zinc-900 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-150"
                  style={{
                    width: `${scanProgress.pct}%`,
                    background: scanProgress.paused
                      ? 'rgba(245,158,11,0.7)'
                      : 'linear-gradient(90deg, #2dd4bf, #5eead4)',
                  }}
                />
              </div>
            </div>

            {/* Files counter */}
            <div className="flex items-center gap-3 px-4 py-3 rounded-sm bg-zinc-900/70 border border-zinc-800">
              <Database className="h-4 w-4 text-teal-500 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-[12px] font-semibold text-zinc-100">
                  {scanProgress.filesProcessed.toLocaleString()}
                  <span className="text-zinc-500 font-normal"> / {scanProgress.totalFiles.toLocaleString()} files</span>
                </div>
                <div className="text-[11px] text-zinc-500 truncate mt-0.5 font-mono">
                  {scanProgress.currentFile || '—'}
                </div>
              </div>
              {!scanProgress.paused && (
                <Loader2 className="h-4 w-4 text-teal-400 shrink-0 animate-spin" />
              )}
            </div>

            {/* Operation pipeline */}
            <div className="space-y-1">
              {SCAN_OPS.map((op) => {
                const done    = scanProgress.pct > op.pctStart + 2;
                const active  = scanProgress.currentOp === op.label;
                return (
                  <div key={op.label} className="flex items-center gap-3 px-3 py-1.5 rounded-sm transition-all"
                    style={{ background: active ? 'rgba(37,181,168,0.06)' : 'transparent' }}>
                    <div className="h-2 w-2 rounded-full shrink-0 transition-all" style={{
                      background: done   ? '#5eead4'
                                : active ? '#f59e0b'
                                         : 'rgba(71,85,105,0.5)',
                      boxShadow: active ? '0 0 6px rgba(245,158,11,0.6)' : 'none',
                    }} />
                    <span className="text-[12px] transition-colors" style={{
                      color: done   ? '#94a3b8'
                           : active ? '#f1f5f9'
                                    : '#475569',
                    }}>
                      {op.label}
                    </span>
                    {done && <CheckCircle2 className="h-3 w-3 text-teal-500 ml-auto shrink-0" />}
                    {active && !scanProgress.paused && (
                      <span className="text-[10px] text-amber-400 ml-auto uppercase tracking-wider">in progress</span>
                    )}
                  </div>
                );
              })}
            </div>

          </section>
        )}

        {/* ── STAGE: knowledge-graph ────────────────────────────────────────── */}
        {stage === 'knowledge-graph' && kgResult && (
          <section data-testid="sie-knowledge-graph" className="p-5 space-y-5 overflow-auto">

            {/* Header badge */}
            <div className="flex items-center gap-3 px-4 py-3 rounded-sm"
              style={{ background: 'rgba(37,181,168,0.08)', border: '1px solid rgba(37,181,168,0.3)' }}>
              <CheckCircle2 className="h-5 w-5 text-teal-400 shrink-0" />
              <div>
                <div className="text-[13px] font-bold text-zinc-100">Project Scan Complete</div>
                <div className="text-[11px] text-zinc-500 mt-0.5">{folderName} · {kgResult.total} files indexed</div>
              </div>
            </div>

            {/* File type breakdown */}
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-400 mb-2">Files Indexed</div>
              <div className="grid grid-cols-2 gap-1.5">
                {kgResult.byType.map((s) => {
                  const Icon = s.cat.icon;
                  return (
                    <div key={s.cat.label} className="flex items-center gap-2 px-3 py-2 rounded-sm bg-zinc-900/60">
                      <Icon className="h-3 w-3 shrink-0" style={{ color: s.cat.color }} />
                      <span className="text-[11px] text-zinc-300">{s.count} {s.count === 1 ? s.cat.label : s.cat.plural}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Knowledge graph status */}
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-400 mb-2">Knowledge Base</div>
              <div className="space-y-2">
                <KGRow icon={Database}  color="#5eead4" label="Knowledge Base Created"    value="✓" />
                <KGRow icon={Brain}     color="#a78bfa" label="Entities Extracted"        value={`${kgResult.entities} entities`} />
                <KGRow icon={Clock}     color="#f59e0b" label="Project Timeline Built"    value={`${kgResult.timeline} events`} />
                <KGRow icon={ListChecks}color="#86efac" label="Action Items Found"        value={`${kgResult.actions} items`} />
                <KGRow icon={Shield}    color="#f87171" label="Risks Identified"          value={`${kgResult.risks} risk${kgResult.risks !== 1 ? 's' : ''}`} />
              </div>
            </div>

            {/* ── Budget capture ──────────────────────────────────────── */}
            <div className="px-4 py-3 rounded-sm" style={{ background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.25)' }}>
              <div className="flex items-center justify-between mb-2">
                <div className="text-[11px] uppercase tracking-[0.18em] text-amber-400/80">Project Budget</div>
                {budget.trim() && !editingBudget && (
                  <button onClick={() => setEditingBudget(true)} className="text-[10px] text-zinc-500 hover:text-zinc-300 underline">Edit</button>
                )}
              </div>

              {budget.trim() && !editingBudget ? (
                /* Compact saved state */
                <div className="flex items-center gap-2">
                  <span className="text-[13px] font-semibold text-zinc-100">${budget} {budgetCurrency}</span>
                  <span className="text-[10px] text-amber-300/60">saved ✓</span>
                </div>
              ) : (
                /* Input */
                <div className="flex items-center gap-2">
                  <span className="text-zinc-400 text-[13px] shrink-0">$</span>
                  <input
                    autoFocus={editingBudget}
                    data-testid="sie-budget-input"
                    type="text"
                    inputMode="numeric"
                    placeholder="e.g. 450,000"
                    value={budget}
                    onChange={(e) => {
                      setBudget(e.target.value);
                      localStorage.setItem(`sie.budget.${project}`, e.target.value);
                    }}
                    onBlur={() => { if (budget.trim()) setEditingBudget(false); }}
                    onKeyDown={(e) => { if (e.key === 'Enter' && budget.trim()) setEditingBudget(false); }}
                    className="flex-1 px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-sm text-zinc-100 text-[13px] focus:outline-none focus:border-amber-500/60"
                  />
                  <select
                    data-testid="sie-budget-currency"
                    value={budgetCurrency}
                    onChange={(e) => { setBudgetCurrency(e.target.value); localStorage.setItem('sie.budgetCurrency', e.target.value); }}
                    className="px-2 py-2 bg-zinc-900 border border-zinc-700 rounded-sm text-zinc-400 text-[12px] focus:outline-none focus:border-amber-500/60"
                  >
                    {['CAD','USD','EUR','GBP','AUD'].map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              )}
            </div>

            {/* ── Ask R2 ────────────────────────────────────────────────── */}
            <div className="space-y-3 pt-1 border-t border-zinc-800/60">
              <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-400">Ask R2 anything about this project</div>

              {/* Quick prompts */}
              <div className="flex flex-wrap gap-1.5">
                {['Summarize risks','List action items','Build timeline','Find stakeholders','Permit requirements','Generate RFI log'].map((p) => (
                  <button
                    key={p}
                    onClick={() => { setR2Query(p); setR2Response(null); }}
                    className="px-2.5 py-1 text-[11px] rounded-full border transition-all"
                    style={{
                      borderColor: r2Query === p ? 'rgba(37,181,168,0.6)' : 'rgba(255,255,255,0.1)',
                      color:       r2Query === p ? '#5eead4' : '#94a3b8',
                      background:  r2Query === p ? 'rgba(37,181,168,0.1)' : 'transparent',
                    }}
                  >
                    {p}
                  </button>
                ))}
              </div>

              {/* Input + submit */}
              <div className="flex gap-2">
                <input
                  data-testid="sie-r2-query"
                  value={r2Query}
                  onChange={(e) => { setR2Query(e.target.value); setR2Response(null); }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && r2Query.trim() && !r2Thinking) {
                      e.preventDefault();
                      void handleR2Query();
                    }
                  }}
                  placeholder="What would you like R2 to do with this project?"
                  className="flex-1 px-3 py-2 bg-zinc-900 border border-zinc-700 rounded-sm text-zinc-100 text-[12px] focus:outline-none focus:border-teal-500/50"
                />
                <button
                  data-testid="sie-r2-ask"
                  onClick={() => void handleR2Query()}
                  disabled={!r2Query.trim() || r2Thinking}
                  className="px-4 py-2 text-[11px] uppercase tracking-[0.14em] font-bold rounded-sm transition disabled:opacity-40"
                  style={{ background: 'rgba(37,181,168,0.15)', border: '1px solid rgba(37,181,168,0.4)', color: '#5eead4' }}
                >
                  {r2Thinking ? '…' : 'Ask'}
                </button>
              </div>

              {/* Response */}
              {(r2Thinking || r2Response) && (
                <div
                  data-testid="sie-r2-response"
                  className="p-4 rounded-sm text-[12px] leading-relaxed overflow-y-auto"
                  style={{ background: 'rgba(15,23,42,0.8)', border: '1px solid rgba(37,181,168,0.2)', maxHeight: 240 }}
                >
                  {r2Thinking ? (
                    <div className="flex items-center gap-2 text-teal-400">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      <span className="text-[11px]">R2 is analyzing the knowledge base…</span>
                    </div>
                  ) : (
                    <div className="text-zinc-300 whitespace-pre-wrap"
                      dangerouslySetInnerHTML={{ __html: (r2Response ?? '').replace(/\*\*(.+?)\*\*/g, '<strong class="text-zinc-100">$1</strong>').replace(/^_(.+)_$/gm, '<em class="text-zinc-500">$1</em>') }}
                    />
                  )}
                </div>
              )}
            </div>

            {/* ── What would you like to do next? ───────────────────────── */}
            <div className="pt-1 border-t border-zinc-800/60">
              <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-400 mb-3">What would you like to do next?</div>
              <div className="grid grid-cols-2 gap-2">
                {[
                  { icon: ListChecks, color: '#86efac', label: 'Review Action Items',    desc: `${kgResult.actions} items found`,          route: 'sie'      },
                  { icon: Shield,     color: '#f87171', label: 'Review Risks',           desc: `${kgResult.risks} risks identified`,        route: 'sie'      },
                  { icon: Brain,      color: '#a78bfa', label: 'Explore Entities',       desc: `${kgResult.entities} entities extracted`,   route: 'sie'      },
                  { icon: FileText,   color: '#fcd34d', label: 'Generate Report',        desc: 'Export project intelligence summary',       route: 'reports'  },
                  { icon: Database,   color: '#5eead4', label: 'Open Knowledge Base',    desc: 'Browse indexed project data',               route: 'sie'      },
                  ...(vault.available && vault.config.vaultPath
                    ? [{ icon: Shield, color: '#5eead4', label: 'Copy to R2 Vault', desc: 'Secure structured copy of project files', route: 'vault' as string | null }]
                    : []),
                  { icon: FolderOpen, color: '#94a3b8', label: 'Ingest Another Folder', desc: 'Scan a second project or subfolder', route: null },
                ].map(({ icon: Icon, color, label, desc, route }) => (
                  <button
                    key={label}
                    data-testid={`sie-next-${label.toLowerCase().replace(/\s+/g, '-')}`}
                    onClick={() => {
                      if (route === null) {
                        setStage('input'); setText(''); setFolderName(''); setFileStats([]); setKgResult(null); setBudget(localStorage.getItem(`sie.budget.${project}`) ?? ''); setBudgetCurrency(localStorage.getItem('sie.budgetCurrency') ?? 'CAD'); setEditingBudget(false);
                        setScanProgress({ filesProcessed: 0, totalFiles: 0, currentFile: '', currentOp: 'Scanning folders', pct: 0, etaSeconds: 0, paused: false });
                      } else if (route === 'vault') {
                        if (!vault.config.vaultPath) setShowVaultSetup(true);
                        else setStage('vault-copy');
                      } else {
                        onClose();
                        onNavigate?.(route);
                      }
                    }}
                    className="flex items-start gap-2.5 p-3 rounded-sm text-left transition-all group"
                    style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.05)'; e.currentTarget.style.borderColor = color + '55'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.02)'; e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)'; }}
                  >
                    <Icon className="h-3.5 w-3.5 mt-0.5 shrink-0" style={{ color }} />
                    <div>
                      <div className="text-[11.5px] font-semibold text-zinc-100 leading-tight">{label}</div>
                      <div className="text-[10.5px] text-zinc-500 mt-0.5 leading-tight">{desc}</div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between pt-1 border-t border-zinc-900">
              <button onClick={() => setStage('folder-preview')}
                className="px-3 py-1.5 text-[10.5px] uppercase tracking-[0.16em] rounded-sm border border-zinc-700 text-zinc-500 hover:text-zinc-300 transition">
                Rescan
              </button>
              <button data-testid="sie-done" onClick={onClose}
                className="px-3 py-1.5 text-[10.5px] uppercase tracking-[0.16em] rounded-sm text-zinc-500 hover:text-zinc-300 transition">
                Close
              </button>
            </div>
          </section>
        )}

        {/* ── STAGE: vault-copy ─────────────────────────────────────────── */}
        {stage === 'vault-copy' && vault.config.vaultPath && kgResult && (
          <section className="p-5 overflow-auto">
            <VaultCopyStage
              sourcePath={sourceFolderPath ?? folderName}
              projectNumber={project || folderName}
              vaultConfig={vault.config}
              totalFiles={kgResult.total}
              onComplete={(projDir) => { void window.r2Vault?.openInExplorer(projDir); }}
              onSkip={() => setStage('knowledge-graph')}
            />
          </section>
        )}

        {/* ── Vault setup modal (desktop) ───────────────────────────────── */}
        {showVaultSetup && (
          <VaultSetupModal
            onClose={() => setShowVaultSetup(false)}
            onSetup={() => { setShowVaultSetup(false); setStage('vault-copy'); }}
          />
        )}
        {stage === 'review' && draft && (
          <section data-testid="sie-review" className="flex-1 overflow-auto p-5 space-y-4">
            <div className="text-[11px] text-zinc-400">
              <b className="text-amber-200">{draft.detectionCount}</b> detection(s) found ·
              Target: <span className="font-mono text-zinc-200">{project || '— none —'}</span>
            </div>
            {grouped.length === 0 && (
              <div className="flex items-center gap-2 px-4 py-3 rounded-sm"
                style={{ background: 'rgba(245,158,11,0.07)', border: '1px solid rgba(245,158,11,0.2)' }}>
                <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0" />
                <span className="text-[12px] text-amber-200/80">
                  No structured entities detected. Try pasting a contact block, email thread, or project brief with names, emails, or project codes.
                </span>
              </div>
            )}
            {grouped.map((g) => {
              const Icon = KIND_META[g.kind].icon;
              return (
                <div key={g.kind} className="border border-zinc-800 rounded-sm">
                  <div className="px-3 py-2 flex items-center gap-2 border-b border-zinc-800 bg-zinc-900/50">
                    <Icon className="h-3.5 w-3.5 text-amber-200" />
                    <span className="text-[11px] uppercase tracking-[0.18em] text-zinc-200">
                      {KIND_META[g.kind].label} · {g.items.length}
                    </span>
                  </div>
                  <ul className="divide-y divide-zinc-900">
                    {g.items.map((d) => {
                      const dec = decisions[d.id];
                      return (
                        <li key={d.id} data-testid={`sie-row-${d.id}`} className="px-3 py-2 flex items-start gap-3">
                          <input type="checkbox" data-testid={`sie-accept-${d.id}`}
                            checked={dec?.accept ?? false}
                            onChange={(ev) => setAccept(d.id, ev.target.checked)}
                            className="mt-1 accent-amber-300" />
                          <div className="flex-1 min-w-0">
                            <DetectionRow d={d} dec={dec} onField={setField} onRole={setRole} />
                          </div>
                          <span className="text-[10px] text-zinc-500 font-mono">{d.confidence}%</span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })}
            <div className="flex items-center justify-end gap-2 pt-2 border-t border-zinc-900">
              <button onClick={() => setStage('input')}
                className="px-3 py-1.5 text-[10.5px] uppercase tracking-[0.16em] rounded-sm border border-zinc-700 text-zinc-300 hover:text-white">
                Back
              </button>
              <button data-testid="sie-approve" onClick={() => void onApprove()}
                disabled={applyMut.isPending}
                className="px-3 py-1.5 text-[10.5px] uppercase tracking-[0.16em] rounded-sm bg-amber-300 text-zinc-900 font-semibold hover:bg-amber-200 transition disabled:opacity-50">
                {applyMut.isPending ? 'Importing…' : 'Approve & Import'}
              </button>
            </div>
          </section>
        )}

        {/* ── STAGE: complete (paste flow) ──────────────────────────────────── */}
        {stage === 'complete' && summary && (
          <section data-testid="sie-summary" className="p-5 space-y-3">
            <div className="border border-emerald-400/30 bg-emerald-500/5 rounded-sm p-4 text-[12px] text-emerald-200">
              <div className="font-semibold text-emerald-100 text-[13px] mb-3 flex items-center gap-2">
                <Inbox className="h-4 w-4" /> Import complete
              </div>
              <ul className="space-y-1.5 text-[11.5px]">
                <li>Organizations created: <b>{summary.organizationsCreated}</b></li>
                <li>Contacts created: <b>{summary.contactsCreated}</b></li>
                <li>Contacts linked to project: <b>{summary.contactsLinked}</b></li>
                <li>Meetings scheduled: <b>{summary.meetingsCreated}</b></li>
                <li>Notes / actions logged: <b>{summary.notesCreated}</b></li>
                {summary.errors.length > 0 && (
                  <li className="text-rose-300 mt-2">
                    {summary.errors.length} warning(s):
                    <ul className="ml-4 list-disc">{summary.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>
                  </li>
                )}
              </ul>
            </div>
            <div className="flex justify-end">
              <button data-testid="sie-done" onClick={onClose}
                className="px-3 py-1.5 text-[10.5px] uppercase tracking-[0.16em] rounded-sm bg-amber-300 text-zinc-900 font-semibold hover:bg-amber-200 transition">
                Done
              </button>
            </div>
          </section>
        )}

      </div>
    </div>,
    document.body,
  );
}

// ── R2 mock response generator ─────────────────────────────────────────────────
function generateR2Response(query: string, kg: KGResult | null, projectNumber: string): string {
  const q   = query.toLowerCase();
  const proj = projectNumber || 'this project';
  const total = kg?.total ?? 0;

  if (/risk|issue|problem|concern/i.test(q)) return [
    `**Risk Summary — ${proj}**`,
    '',
    `R2 identified ${kg?.risks ?? 3} potential risk indicators across ${total} indexed files:`,
    '',
    '• **Schedule Risk** — 2 emails contain deadline language without confirmed dates',
    '• **Scope Risk** — Discrepancy detected between drawing revision A3 and spec Division 27',
    '• **Procurement Risk** — Lead time for telecom equipment not confirmed in any document',
    `• **Budget Risk** — ${kg?.actions ?? 5} action items flagged without cost estimates`,
    '',
    '_R2 recommends scheduling a risk review meeting before mobilization._',
  ].join('\n');

  if (/action item|todo|task|follow.?up/i.test(q)) return [
    `**Action Items — ${proj}**`,
    '',
    `${kg?.actions ?? 8} action items extracted from ingested documents:`,
    '',
    '1. Confirm permit submission date with municipality — **Owner** (no deadline set)',
    '2. Issue revised cable schedule to contractor — **EIC** (due at 30% design)',
    '3. Resolve IDF room clearance conflict on Floor 3 — **Designer**',
    '4. Obtain landlord approval for rooftop antenna mount — **PM**',
    '5. Confirm fibre pathway routing through parkade — **Site**',
    '',
    '_Would you like me to export these as a tracked action register?_',
  ].join('\n');

  if (/timeline|schedule|milestone|gantt/i.test(q)) return [
    `**Project Timeline — ${proj}**`,
    '',
    `R2 reconstructed ${kg?.timeline ?? 6} timeline events from ${total} documents:`,
    '',
    '| Milestone | Date | Source |',
    '|---|---|---|',
    '| Project Kickoff | Jan 14, 2026 | kickoff-email.eml |',
    '| 30% Design Complete | Feb 28, 2026 | scope.pdf |',
    '| Permit Submission | Mar 15, 2026 | schedule.xlsx |',
    '| Tender Issue | Apr 1, 2026 | project-brief.docx |',
    '| Site Mobilization | May 12, 2026 | contract.pdf |',
    '| Substantial Completion | Oct 30, 2026 | scope.pdf |',
    '',
    '_Timeline confidence: 73% — some dates inferred from context._',
  ].join('\n');

  if (/stakeholder|contact|team|personnel|who/i.test(q)) return [
    `**Stakeholder Register — ${proj}**`,
    '',
    `${kg?.entities ?? 12} entities extracted from project documents:`,
    '',
    '**Owner / Client**',
    '• John Smith — VP Facilities (john.smith@client.com)',
    '',
    '**Design Team**',
    '• R2 Engine — Prime Consultant (Lead: RCDD)',
    '• MEP Inc. — Mechanical / Electrical',
    '',
    '**Authorities Having Jurisdiction**',
    '• City of Calgary — Building Permit',
    '• TELUS — Utility Coordination',
    '',
    '_Export full register as CSV or import into project contacts?_',
  ].join('\n');

  if (/permit|approval|authority|jurisdiction|ahj/i.test(q)) return [
    `**Permit & Approval Requirements — ${proj}**`,
    '',
    'Based on ingested documents, R2 identified the following:',
    '',
    '• **Building Permit** — Required. Drawings reference permit set submission.',
    '• **Development Permit** — Conditional. Verify with municipality.',
    '• **Right-of-Way** — Required for underground conduit along 4th Ave.',
    '• **Utility Coordination** — TELUS, Shaw, and Fortis flagged in scope documents.',
    '• **Fire Department Review** — Referenced in Division 21 spec section.',
    '',
    '_R2 recommends creating a permits tracking log. Would you like me to generate one?_',
  ].join('\n');

  if (/rfi|request for information|drawing|spec/i.test(q)) return [
    `**RFI Log — ${proj}**`,
    '',
    `R2 identified ${Math.max(3, Math.round((kg?.total ?? 10) * 0.04))} potential RFI triggers:`,
    '',
    '| # | Subject | Drawing Ref | Priority |',
    '|---|---|---|---|',
    '| RFI-001 | IDF room dimensions conflict with arch | A-201 vs T-101 | High |',
    '| RFI-002 | Conduit routing through structural wall | S-301 | Medium |',
    '| RFI-003 | Grounding electrode location | E-101 | Medium |',
    '',
    '_Would you like me to format this as a formal RFI register?_',
  ].join('\n');

  if (/summar|overview|brief|report|what.*project/i.test(q)) return [
    `**Project Intelligence Summary — ${proj}**`,
    '',
    `R2 analyzed **${total} files** and built a complete knowledge graph:`,
    '',
    `• **${kg?.entities ?? 0} entities** extracted (contacts, organizations, locations, project codes)`,
    `• **${kg?.actions ?? 0} action items** identified across emails and meeting notes`,
    `• **${kg?.timeline ?? 0} timeline events** reconstructed`,
    `• **${kg?.risks ?? 0} risk indicators** flagged for review`,
    '',
    '**Key findings:**',
    '— Project is in design phase with active stakeholder communication',
    '— Permit submission is the next critical milestone',
    '— 3 open items require owner decision before 30% design',
    '',
    '_Full report available for export. What would you like to dig into?_',
  ].join('\n');

  // Default
  return [
    `**R2 Response — ${proj}**`,
    '',
    `I've analyzed your project knowledge base (${total} files, ${kg?.entities ?? 0} entities). Here's what I found relevant to your request:`,
    '',
    `Based on the ingested documents, ${query.trim().endsWith('?') ? 'the answer involves' : 'R2 recommends focusing on'}:`,
    '',
    '• Review the extracted action items for immediate next steps',
    '• Cross-reference the timeline events with current project status',
    '• Check the risk register for items requiring owner input',
    '',
    '_Type a more specific question or choose a suggested prompt for detailed analysis._',
  ].join('\n');
}

// ─────────────────────────────────────────────────────────────────────────────
interface KGResult {
  total: number;
  byType: FileStat[];
  entities: number;
  actions: number;
  risks: number;
  timeline: number;
}

// ── Knowledge graph row ────────────────────────────────────────────────────────
function KGRow({ icon: Icon, color, label, value }: {
  icon: typeof FileText; color: string; label: string; value: string;
}): JSX.Element {
  return (
    <div className="flex items-center gap-3 px-3 py-2 rounded-sm bg-zinc-900/60">
      <Icon className="h-3.5 w-3.5 shrink-0" style={{ color }} />
      <span className="flex-1 text-[12px] text-zinc-200">{label}</span>
      <span className="text-[11px] font-semibold" style={{ color }}>{value}</span>
    </div>
  );
}

// ── Detection row (contact / org / generic) ────────────────────────────────────
function DetectionRow({ d, dec, onField, onRole }: {
  d: Detection;
  dec: ApprovalDecision | undefined;
  onField: (id: string, key: string, value: string) => void;
  onRole:  (id: string, role: SieRole) => void;
}): JSX.Element {
  const p = { ...(d.payload as Record<string, string | undefined>), ...((dec?.payload as Record<string, string | undefined>) ?? {}) };
  if (d.kind === 'contact') {
    return (
      <div className="grid grid-cols-2 gap-2">
        <Field label="Name"  value={p.fullName ?? ''} onChange={(v) => onField(d.id, 'fullName', v)} />
        <Field label="Email" value={p.email ?? ''}    onChange={(v) => onField(d.id, 'email', v)} />
        <Field label="Phone" value={p.phone ?? ''}    onChange={(v) => onField(d.id, 'phone', v)} />
        <Field label="Title" value={p.title ?? ''}    onChange={(v) => onField(d.id, 'title', v)} />
        <Field label="Org"   value={p.organization ?? ''} onChange={(v) => onField(d.id, 'organization', v)} />
        <label className="block">
          <span className="block text-[10px] uppercase tracking-[0.16em] text-zinc-500 mb-0.5">Role</span>
          <select data-testid={`sie-role-${d.id}`}
            value={dec?.role ?? (d.payload as { inferredRole?: SieRole }).inferredRole ?? 'other'}
            onChange={(e) => onRole(d.id, e.target.value as SieRole)}
            className="w-full px-2 py-1 bg-zinc-900 border border-zinc-700 rounded-sm text-zinc-100 text-[11px]">
            {SIE_ROLE_TAXONOMY.map((r) => <option key={r} value={r}>{r.replace(/_/g, ' ')}</option>)}
          </select>
        </label>
      </div>
    );
  }
  if (d.kind === 'organization') {
    return <Field label="Name" value={p.name ?? ''} onChange={(v) => onField(d.id, 'name', v)} />;
  }
  return <div className="text-[12px] text-zinc-200 font-mono break-words">{d.source}</div>;
}

function Field({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }): JSX.Element {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-[0.16em] text-zinc-500 mb-0.5">{label}</span>
      <input value={value} onChange={(e) => onChange(e.target.value)}
        className="w-full px-2 py-1 bg-zinc-900 border border-zinc-700 rounded-sm text-zinc-100 text-[11px]" />
    </label>
  );
}
